import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Package, Gem, Loader2, AlertCircle, RotateCcw, TrendingUp, ChevronLeft, ChevronRight, FileText } from 'lucide-react'
import confetti from 'canvas-confetti'
import useCasesStore from '../store/useCasesStore'
import useAppStore from '../store/useAppStore'
import useInventoryStore from '../store/useInventoryStore'
import { inventoryApi } from '../services'
import { formatNumber } from '../utils/formatters'
import Loader from '../components/Loader'
import ConfirmModal from '../components/ConfirmModal'
import { toast } from 'react-hot-toast'
import useAudioManager from '../hooks/useAudioManager'

const CaseOpeningMulti = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  
  const { 
    currentCase,
    loadCaseDetails,
    openCaseMultiple, 
    isOpeningMultiple, 
    openingMultipleResults,
    error,
    clearMultipleResults,
    clearError 
  } = useCasesStore()
  const { loadProfile } = useAppStore()
  
  const caseId = id
  const caseData = currentCase
  
  // Получаем количество открытий из state навигации
  const openCount = location.state?.count || 2
  
  // Состояния для анимации появления предметов
  const [displayedItems, setDisplayedItems] = useState([]) // Массив уже показанных предметов
  const [currentItemIndex, setCurrentItemIndex] = useState(0) // Индекс текущего показываемого предмета
  const [isAnimationComplete, setIsAnimationComplete] = useState(false) // Флаг завершения анимации
  const [activeSlide, setActiveSlide] = useState(0) // Активный слайд в карусели
  const [isCardAnimating, setIsCardAnimating] = useState(false) // Флаг анимации карточки
  
  // Состояния для продажи предметов
  const [soldItemIds, setSoldItemIds] = useState(new Set()) // Set проданных ID предметов
  const [isSellingAll, setIsSellingAll] = useState(false) // Флаг загрузки кнопки "Продать всё"
  const [isSellingItem, setIsSellingItem] = useState(false) // Флаг загрузки кнопки продажи отдельного предмета
  const [showConfirmModal, setShowConfirmModal] = useState(false) // Состояние модального окна подтверждения
  
  // Флаги для предотвращения повторного воспроизведения
  const hasPlayedStartSound = useRef(false)
  const hasPlayedFinalSound = useRef(false)
  const hasStartedOpening = useRef(false)
  
  // Инициализация Audio Manager через глобальный audioService
  const { playSound, stopAllSounds } = useAudioManager({
    win: 'caseMultiWin',
    sell: 'sell',
    start: 'caseMultiStart',
    goodWin: 'caseMultiWinGood',
    final: 'caseMultiFinal'
  })
  
  // Функция для сокращения состояния износа
  const shortenWear = (text) => {
    return text
      .replace(/\(Factory New\)/gi, 'FN')
      .replace(/\(Minimal Wear\)/gi, 'MW')
      .replace(/\(Field-Tested\)/gi, 'FT')
      .replace(/\(Well-Worn\)/gi, 'WW')
      .replace(/\(Battle-Scarred\)/gi, 'BS')
  }

  // Остановка всех звуков при уходе со страницы
  useEffect(() => {
    return () => {
      stopAllSounds()
      console.log('🔇 CaseOpeningMulti: все звуки остановлены при размонтировании')
    }
  }, [])

  // Проверка легитимности открытия
  useEffect(() => {
    if (!location.state?.allowOpening) {
      console.log('⚠️ Попытка открыть кейсы без флага allowOpening - редирект на /cases')
      navigate('/cases', { replace: true })
      return
    }
    
    // Очищаем флаг из истории
    window.history.replaceState({}, document.title)
  }, [location, navigate])

  // Звуки теперь управляются через useAudioManager (Web Audio API)
  // Предзагрузка происходит автоматически в хуке

  // Очистка результатов при монтировании компонента
  useEffect(() => {
    clearMultipleResults()
    clearError()
    hasPlayedStartSound.current = false
    hasPlayedFinalSound.current = false
    hasStartedOpening.current = false
    
    // Сбрасываем флаги при размонтировании для следующего открытия
    return () => {
      hasPlayedStartSound.current = false
      hasPlayedFinalSound.current = false
      hasStartedOpening.current = false
    }
  }, [])

  // Загрузка деталей кейса
  useEffect(() => {
    if (caseId && !currentCase) {
      loadCaseDetails(caseId)
    }
  }, [caseId, currentCase, loadCaseDetails])

  // Запуск открытия кейсов после загрузки деталей
  useEffect(() => {
    if (caseData && !isOpeningMultiple && !openingMultipleResults && !hasStartedOpening.current) {
      hasStartedOpening.current = true
      handleOpenCases()
    }
  }, [caseData, isOpeningMultiple, openingMultipleResults])

  // Функция открытия кейсов
  const handleOpenCases = async () => {
    try {
      await openCaseMultiple(caseId, openCount)
    } catch (error) {
      console.error('Failed to open cases:', error)
    }
  }

  // Функция воспроизведения звука начала открытия когда получены результаты
  useEffect(() => {
    // Звуки предзагружены глобально, воспроизводим сразу
    if (openingMultipleResults && openingMultipleResults.length > 0 && !hasPlayedStartSound.current) {
      playSound('start')
      hasPlayedStartSound.current = true
    }
  }, [openingMultipleResults, playSound])

  // Воспроизведение финального звука через 2000мс после завершения анимации
  useEffect(() => {
    if (isAnimationComplete && !hasPlayedFinalSound.current) {
      const timer = setTimeout(() => {
        playSound('final')
        hasPlayedFinalSound.current = true // Отмечаем что финальный звук воспроизведен
      }, 2000)
      
      return () => clearTimeout(timer)
    }
  }, [isAnimationComplete, playSound])

  // Анимация появления предметов по одному
  useEffect(() => {
    if (!openingMultipleResults || openingMultipleResults.length === 0) return
    
    // Если все предметы уже показаны
    if (currentItemIndex >= openingMultipleResults.length) {
      setIsAnimationComplete(true)
      // Обновляем инвентарь после показа всех предметов
      const { loadInventory } = useInventoryStore.getState()
      loadInventory()
      return
    }
    
    // Задержка 2500мс перед показом следующего предмета
    const timer = setTimeout(() => {
      const newItem = openingMultipleResults[currentItemIndex]
      
      // Запускаем анимацию появления
      setIsCardAnimating(true)
      
      setDisplayedItems(prev => [...prev, newItem])
      setCurrentItemIndex(prev => prev + 1)
      setActiveSlide(currentItemIndex) // Переключаем на новый слайд
      
      // Проверяем профит
      const profitString = newItem?.win?.profit || '0%'
      const profitPercent = parseInt(profitString.replace(/[^0-9-]/g, ''))
      
      // Если профит > 6% - воспроизводим звук хорошего выигрыша и конфетти
      if (profitPercent > 6) {
        playSound('goodWin')
        triggerConfetti()
      } else {
        // Воспроизводим звук продажи
        playSound('win')
      }
      
      // Сбрасываем флаг анимации через 600мс (длительность анимации)
      setTimeout(() => {
        setIsCardAnimating(false)
      }, 600)
    }, 2500)
    
    return () => clearTimeout(timer)
  }, [currentItemIndex, openingMultipleResults])

  // Все звуки теперь воспроизводятся через playSound() из useAudioManager
  // Это обеспечивает надежное воспроизведение через Web Audio API

  // Функция запуска конфетти
  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#00FF7F', '#4EFFC6', '#FFD700', '#FF6B6B', '#4ECDC4']
    })
  }

  // Навигация по карусели
  const goToSlide = (index) => {
    if (index >= 0 && index < displayedItems.length) {
      setActiveSlide(index)
    }
  }

  const goToPrevSlide = () => {
    goToSlide(activeSlide - 1)
  }

  const goToNextSlide = () => {
    goToSlide(activeSlide + 1)
  }

  // Продажа предмета
  const handleSellItem = async (item) => {
    const itemId = item?.id || item?.inventory_id
    
    if (!itemId) {
      toast.error(t('caseOpeningMulti.sellItemError'))
      return
    }

    try {
      setIsSellingItem(true)
      await inventoryApi.saleItem(itemId)
      playSound('sell')
      await loadProfile()
      
      // Добавляем проданный предмет в Set
      setSoldItemIds(prev => new Set([...prev, itemId]))
      
      toast.success(`Продано за ${item.win?.cost} кристаллов`, {
        duration: 2000
      })
      
      // Удаляем проданный предмет из отображаемых
      const newDisplayedItems = displayedItems.filter(i => (i.id || i.inventory_id) !== itemId)
      setDisplayedItems(newDisplayedItems)
      
      // Если это был последний предмет - редиректим на страницу кейса
      if (newDisplayedItems.length === 0) {
        // Очищаем результаты множественного открытия
        clearMultipleResults()
        // Обновляем баланс перед переходом
        await loadProfile()
        // Переходим на страницу кейса без флага allowOpening
        navigate(`/cases/${id}`, { replace: true })
        return
      }
      
      // Если удалили активный слайд, переключаемся на предыдущий
      if (activeSlide >= newDisplayedItems.length && activeSlide > 0) {
        setActiveSlide(activeSlide - 1)
      }
    } catch (error) {
      console.error('Sell error:', error)
      toast.error(t('caseOpeningMulti.sellItemError'))
    } finally {
      setIsSellingItem(false)
    }
  }

  // Открыть модальное окно подтверждения
  const handleSellAllClick = () => {
    setShowConfirmModal(true)
  }
  
  // Продажа всех непроданных предметов (вызывается после подтверждения)
  const handleSellAllItems = async () => {
    // Получаем ID всех непроданных предметов
    const unsoldItems = displayedItems.filter(item => {
      const itemId = item?.id || item?.inventory_id
      return itemId && !soldItemIds.has(itemId)
    })
    
    if (unsoldItems.length === 0) {
      toast.error(t('inventory.sellAllError'))
      return
    }
    
    const itemIds = unsoldItems.map(item => item?.id || item?.inventory_id)
    
    try {
      setIsSellingAll(true)
      setShowConfirmModal(false) // Закрываем модалку
      await inventoryApi.saleSelectedItems(itemIds)
      playSound('sell')
      await loadProfile()
      
      // Добавляем все проданные ID в Set
      setSoldItemIds(prev => new Set([...prev, ...itemIds]))
      
      toast.success(t('inventory.sellAllSuccess'))
      
      // Очищаем результаты множественного открытия
      clearMultipleResults()
      // Переходим на страницу кейса без флага allowOpening
      navigate(`/cases/${id}`, { replace: true })
    } catch (error) {
      console.error('Sell all error:', error)
      toast.error(t('inventory.sellAllError'))
    } finally {
      setIsSellingAll(false)
    }
  }

  // Обработчик "Открыть ещё"
  const handleOpenAgain = async () => {
    // Обновляем профиль пользователя (баланс) перед переходом
    await loadProfile()
    // Переходим на страницу кейса без флага allowOpening
    // Очистка произойдет автоматически при размонтировании компонента
    navigate(`/cases/${id}`)
  }

  // Обработчик "В инвентарь"
  const handleBackToInventory = () => {
    clearMultipleResults()
    const { loadInventory } = useInventoryStore.getState()
    loadInventory()
    navigate('/inventory')
  }

  // Показываем ошибку
  if (error) {
    return (
      <div className="min-h-screen bg-dark-primary text-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">{t('caseOpeningMulti.openingError')}</h2>
          <p className="text-text-secondary mb-6">{error}</p>
          <div className="space-y-3">
            <button 
              onClick={handleOpenCases}
              className="w-full bg-accent-primary text-dark-primary font-bold py-3 rounded-lg hover:opacity-90 transition-all"
            >
              {t('caseOpeningMulti.retryOpening')}
            </button>
            <button 
              onClick={() => navigate('/cases')}
              className="w-full bg-transparent border border-text-secondary text-text-secondary font-semibold py-3 rounded-lg hover:bg-text-secondary/10 transition-all"
            >
              {t('caseOpeningMulti.returnToCases')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Показываем загрузку
  if (!caseData || isOpeningMultiple || (!openingMultipleResults && !error)) {
    return (
      <div className="min-h-screen bg-dark-primary text-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-accent-primary mx-auto mb-4" size={48} />
        </div>
      </div>
    )
  }

  // Основной контент с каруселью
  return (
    <>
      {/* CSS анимация для карточки */}
      <style>{`
        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.8) translateY(20px);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.05) translateY(-5px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
      
      <div 
        className="min-h-screen text-white overflow-hidden bg-center bg-no-repeat lg:relative lg:left-1/2 lg:w-screen lg:max-w-none lg:-translate-x-1/2 lg:bg-cover"
        style={{
          backgroundImage: 'url(/images/roulette-background.png)',
          backgroundAttachment: 'fixed'
        }}
      >
      <div className="flex flex-col items-center justify-center min-h-screen p-6">
        {/* Заголовок */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-accent-primary mb-2">
            {isAnimationComplete ? t('caseOpeningMulti.allCasesOpened') : t('caseOpeningMulti.openingCases')}
          </h1>
          {/* <p className="text-text-secondary text-lg">
            {isAnimationComplete 
              ? `Показано ${displayedItems.length} предметов`
              : `Открыто ${displayedItems.length} из ${openCount}`
            }
          </p> */}
        </div>

        {/* Карусель с предметами */}
        {displayedItems.length > 0 && (
          <div className="relative w-full max-w-md mb-8">
            {/* Основная карточка предмета */}
            <div 
              className={`relative bg-surface rounded-lg p-6 border-2 border-accent-primary/50 shadow-[0_0_30px_rgba(0,255,127,0.3)] transition-all duration-500 ${
                isCardAnimating 
                  ? 'animate-[fadeInScale_0.6s_ease-out]' 
                  : ''
              }`}
              style={{
                animation: isCardAnimating ? 'fadeInScale 0.6s ease-out' : 'none'
              }}
            >
              {/* Номер открытия */}
              <div className="absolute top-2 left-2 bg-accent-primary/20 border border-accent-primary/50 rounded-md px-2 py-1">
                <span className="text-accent-primary font-bold text-sm">#{activeSlide + 1}</span>
              </div>
              
              <div className="text-center">
                {/* Изображение предмета */}
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <div className="absolute inset-0 bg-accent-primary/20 rounded-lg blur-md animate-pulse"></div>
                  <div className="relative w-full h-full bg-dark-primary rounded-lg border border-accent-primary/50 overflow-hidden">
                    <img 
                      src={displayedItems[activeSlide]?.win?.img} 
                      alt={displayedItems[activeSlide]?.win?.name}
                      className="w-full h-full object-contain p-2"
                    />
                  </div>
                </div>
                
                {/* Название предмета */}
                <h3 className="text-xl font-bold text-center mb-2">
                  {displayedItems[activeSlide]?.win?.name?.includes('|') ? (
                    <>
                      <div className="text-white">
                        {displayedItems[activeSlide].win.name.split('|')[0].trim()}
                      </div>
                      <div className="text-text-secondary text-base mt-1 font-normal">
                        {shortenWear(displayedItems[activeSlide].win.name.split('|')[1].trim())}
                      </div>
                    </>
                  ) : (
                    <div className="text-white">
                      {shortenWear(displayedItems[activeSlide]?.win?.name)}
                    </div>
                  )}
                </h3>
                
                {/* Цена и профит */}
                <div className="flex items-center justify-center gap-3 text-accent-primary font-bold text-lg">
                  <Gem size={20} />
                  <span>{displayedItems[activeSlide]?.win?.cost}</span>
                  
                  {displayedItems[activeSlide]?.win?.profit && (
                    <span 
                      className={`px-2 py-1 rounded-md text-sm font-medium ${
                        displayedItems[activeSlide].win.profit.startsWith('+') 
                          ? 'bg-accent-primary text-dark-primary' 
                          : 'bg-gray-600 text-gray-300'
                      }`}
                    >
                      {displayedItems[activeSlide].win.profit}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Кнопки навигации */}
            {displayedItems.length > 1 && (
              <>
                <button
                  onClick={goToPrevSlide}
                  disabled={activeSlide === 0}
                  className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    activeSlide === 0
                      ? 'bg-surface/50 text-text-secondary/50 cursor-not-allowed'
                      : 'bg-accent-primary text-dark-primary hover:opacity-90'
                  }`}
                >
                  <ChevronLeft size={24} />
                </button>
                
                <button
                  onClick={goToNextSlide}
                  disabled={activeSlide === displayedItems.length - 1}
                  className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    activeSlide === displayedItems.length - 1
                      ? 'bg-surface/50 text-text-secondary/50 cursor-not-allowed'
                      : 'bg-accent-primary text-dark-primary hover:opacity-90'
                  }`}
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}

            {/* Индикаторы слайдов - показываем все сразу */}
            <div className="flex justify-center gap-2 mt-4">
              {Array.from({ length: openCount }).map((_, index) => {
                // Определяем состояние индикатора
                const isOpened = index < displayedItems.length // Уже открыт
                const isActive = index === activeSlide && isOpened // Активный сейчас
                const isPending = index >= displayedItems.length // Еще не открыт
                
                return (
                  <button
                    key={index}
                    onClick={() => isOpened && goToSlide(index)}
                    disabled={isPending}
                    className={`h-2 rounded-full transition-all ${
                      isActive
                        ? 'bg-accent-primary w-6' // Активный - зеленый и длинный
                        : isOpened
                        ? 'bg-accent-primary/50 w-2 hover:bg-accent-primary/70 cursor-pointer' // Открытый - зеленый полупрозрачный
                        : 'bg-text-secondary/20 w-2 cursor-not-allowed' // Еще не открыт - серый
                    }`}
                  />
                )
              })}
            </div>
          </div>
        )}

        {/* Кнопки действий - показываем только после завершения анимации */}
        {isAnimationComplete && displayedItems.length > 0 && (
          <div className="space-y-4 w-full max-w-md mx-auto">
            {/* Кнопки для текущего предмета */}
            <div className="space-y-3">
              <button 
                onClick={() => handleSellItem(displayedItems[activeSlide])}
                disabled={isSellingItem}
                className="w-full h-14 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg shadow-accent-primary/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSellingItem ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    <span>{t('inventory.selling')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('caseOpeningMulti.sellFor')}</span>
                    <Gem size={20} />
                    <span>{displayedItems[activeSlide]?.win?.cost}</span>
                  </>
                )}
              </button>
              
              <button 
                onClick={handleSellAllClick}
                disabled={isSellingAll || displayedItems.length === 0}
                className="w-full h-14 bg-surface border border-accent-primary/30 text-white font-semibold rounded-lg hover:border-accent-primary/50 hover:bg-surface/80 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSellingAll ? (
                  <>
                    <Loader2 size={20} className="animate-spin text-accent-primary" />
                    {t('inventory.sellingAll')}
                  </>
                ) : (
                  <>
                    <Gem size={20} className="text-accent-primary" />
                    {t('inventory.sellAll')} <Gem size={16} className="text-accent-primary" /> {displayedItems.reduce((sum, item) => sum + (parseInt(item.win?.cost) || 0), 0).toLocaleString('ru-RU')}
                  </>
                )}
              </button>

              <button 
                onClick={() => navigate('/contract')}
                className="w-full h-14 bg-surface border border-accent-primary/30 text-white font-semibold rounded-lg hover:border-accent-primary/50 hover:bg-surface/80 transition-all flex items-center justify-center gap-2"
              >
                <FileText size={20} className="text-accent-primary" />
                {t('caseOpeningMulti.goToContracts')}
              </button>
            </div>
            
            {/* Вторичные действия */}
            <div className="flex items-center justify-center gap-4 pt-2 text-sm">
              <button 
                onClick={handleOpenAgain}
                className="flex items-center gap-1.5 text-accent-primary hover:text-accent-secondary transition-colors"
              >
                <RotateCcw size={16} />
                <span className="whitespace-nowrap">{t('caseOpeningMulti.openMore')}</span>
              </button>
              
              <span className="text-text-secondary/50">|</span>
              
              <button 
                onClick={handleBackToInventory}
                className="flex items-center gap-1.5 text-text-secondary hover:text-white transition-colors"
              >
                <Package size={16} />
                <span className="whitespace-nowrap">{t('caseOpeningMulti.toInventory')}</span>
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
      
      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleSellAllItems}
        title={t('inventory.confirmSellAllTitle')}
        message={t('inventory.confirmSellAllMessage', { 
          count: displayedItems.filter(item => {
            const itemId = item?.id || item?.inventory_id
            return itemId && !soldItemIds.has(itemId)
          }).length,
          total: formatNumber(displayedItems.filter(item => {
            const itemId = item?.id || item?.inventory_id
            return itemId && !soldItemIds.has(itemId)
          }).reduce((sum, item) => sum + (parseInt(item.win?.cost) || 0), 0))
        })}
        confirmText={t('inventory.confirmSellAllButton')}
        cancelText={t('common.cancel')}
        isLoading={isSellingAll}
      />
    </>
  )
}

export default CaseOpeningMulti
